import { useEffect, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Code,
  Flex,
  HStack,
  Heading,
  IconButton,
  Input,
  SimpleGrid,
  Spinner,
  Stack,
  Switch,
  Text,
  Textarea,
  Tooltip,
  useToast,
  Image,
} from "@chakra-ui/react";
import LogoUpload from "../components/LogoUpload";
import { PricingFields } from "../components/PricingFields";
import OriginEventsList from "../components/OriginEventsList";
import { ArrowBackIcon, CopyIcon } from "@chakra-ui/icons";
import { useNavigate, useParams } from "react-router-dom";
import {
  OriginsApi,
  type OriginDetail as OriginDetailT,
  type OriginPatch,
} from "../api/origins";
import type { Pricing } from "../api/events";
import { fmtDate } from "../utils/datetime";
import logger from "../analytics/logger";

function normalizeSlug(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9-]/g, "");
}

function eq(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

function buildPatch(orig: OriginDetailT, draft: OriginDetailT): OriginPatch {
  const patch: OriginPatch = {};
  if (!eq(orig.slug, draft.slug)) patch.slug = draft.slug;
  if (!eq(orig.display_name, draft.display_name)) patch.display_name = draft.display_name;
  if (!eq(orig.welcome_headline, draft.welcome_headline))
    patch.welcome_headline = draft.welcome_headline;
  if (!eq(orig.welcome_body, draft.welcome_body)) patch.welcome_body = draft.welcome_body;
  if (!eq(orig.button_text, draft.button_text)) patch.button_text = draft.button_text;
  if (!eq(orig.logo_url, draft.logo_url)) patch.logo_url = draft.logo_url;
  if (!eq(orig.mobile_logo_url, draft.mobile_logo_url))
    patch.mobile_logo_url = draft.mobile_logo_url;
  if (!eq(orig.default_pricing, draft.default_pricing) && draft.default_pricing)
    patch.default_pricing = draft.default_pricing;
  return patch;
}

export default function OriginDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [orig, setOrig] = useState<OriginDetailT | null>(null);
  const [draft, setDraft] = useState<OriginDetailT | null>(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    OriginsApi.get(id)
      .then((d) => {
        setOrig(d);
        setDraft(d);
      })
      .catch((e) => {
        logger.error("origin_get_failed", e);
        setError(e?.response?.data?.error ?? "Failed to load origin");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    void (async () => {
      await load();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const cancel = () => {
    if (orig) setDraft(orig);
    setEditing(false);
    setError(null);
  };

  const back = () => {
    cancel();
    navigate("/origins");
  };

  const save = async () => {
    if (!orig || !draft) return;
    const patch = buildPatch(orig, draft);
    if (Object.keys(patch).length === 0) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await OriginsApi.update(id, patch);
      setOrig(updated);
      setDraft(updated);
      setEditing(false);
    } catch (e: unknown) {
      logger.error("origin_update_failed", e);
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Failed to save changes";
      setError(msg === "slug_taken" ? "That slug is already taken." : msg);
    } finally {
      setSaving(false);
    }
  };

  const copyId = async () => {
    if (!orig) return;
    try {
      await navigator.clipboard.writeText(orig.id);
      toast({ status: "success", title: "Copied Origin ID", duration: 2000 });
    } catch (e) {
      logger.error("origin_copy_id_failed", e);
      toast({ status: "error", title: "Copy failed", duration: 2500 });
    }
  };

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner />
      </Box>
    );
  }
  if (!orig || !draft) {
    return (
      <Box p={6}>
        <Button leftIcon={<ArrowBackIcon />} variant="ghost" onClick={back} mb={4}>
          Back
        </Button>
        <Alert status="error">
          <AlertIcon />
          <AlertDescription>{error ?? "Origin not found"}</AlertDescription>
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <Flex align="center" justify="space-between" mb={4}>
        <HStack>
          <IconButton aria-label="Back" icon={<ArrowBackIcon />} variant="ghost" onClick={back} />
          <Heading size="lg">{orig.display_name || orig.slug}</Heading>
        </HStack>
        <HStack>
          {editing ? (
            <>
              <Button size="sm" variant="ghost" onClick={cancel} isDisabled={saving}>
                Cancel
              </Button>
              <Button size="sm" colorScheme="blue" onClick={save} isLoading={saving}>
                Save
              </Button>
            </>
          ) : (
            <HStack>
              <Text fontSize="sm" color="gray.600">
                Edit
              </Text>
              <Switch isChecked={editing} onChange={(e) => setEditing(e.target.checked)} />
            </HStack>
          )}
        </HStack>
      </Flex>

      {error && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {editing ? (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <Stack spacing={3}>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Slug
              </Text>
              <Input
                size="sm"
                value={draft.slug}
                onChange={(e) => setDraft({ ...draft, slug: normalizeSlug(e.target.value) })}
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Used in URL https://www.humancreator.com/{draft.slug || "{slug}"}
              </Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Display name
              </Text>
              <Input
                size="sm"
                value={draft.display_name ?? ""}
                onChange={(e) =>
                  setDraft({ ...draft, display_name: e.target.value || null })
                }
              />
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Welcome headline
              </Text>
              <Input
                size="sm"
                value={draft.welcome_headline ?? ""}
                onChange={(e) =>
                  setDraft({ ...draft, welcome_headline: e.target.value || null })
                }
              />
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Welcome body
              </Text>
              <Textarea
                size="sm"
                rows={4}
                value={draft.welcome_body ?? ""}
                onChange={(e) =>
                  setDraft({ ...draft, welcome_body: e.target.value || null })
                }
              />
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Button text
              </Text>
              <Input
                size="sm"
                value={draft.button_text}
                onChange={(e) => setDraft({ ...draft, button_text: e.target.value })}
              />
            </Box>
            <LogoUpload
              label="Logo URL"
              value={draft.logo_url}
              onChange={(u) => setDraft({ ...draft, logo_url: u })}
            />
            <LogoUpload
              label="Mobile logo URL"
              helperText="optimal image size is 300 x 200"
              previewRatio={3 / 2}
              value={draft.mobile_logo_url}
              onChange={(u) => setDraft({ ...draft, mobile_logo_url: u })}
            />
            <Box>
              <Text fontWeight="semibold" mb={1}>
                Default pricing
              </Text>
              <Text fontSize="xs" color="gray.500" mb={2}>
                Applies to all of this origin's events unless overridden.
              </Text>
              <PricingFields
                value={draft.default_pricing}
                onChange={(p: Pricing) => setDraft({ ...draft, default_pricing: p })}
                editing
              />
            </Box>
          </Stack>

          <Stack spacing={3}>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Origin ID
              </Text>
              <HStack>
                <Code fontSize="sm" p={2} borderRadius="md" wordBreak="break-all">
                  {orig.id}
                </Code>
                <Tooltip label="Copy Origin ID">
                  <IconButton
                    aria-label="Copy Origin ID"
                    icon={<CopyIcon />}
                    size="sm"
                    variant="ghost"
                    onClick={copyId}
                  />
                </Tooltip>
              </HStack>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Created
              </Text>
              <Text>{fmtDate(orig.created_at)}</Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Updated
              </Text>
              <Text>{fmtDate(orig.updated_at)}</Text>
            </Box>
          </Stack>
        </SimpleGrid>
      ) : (
        <Stack spacing={3}>
          <Flex align="center" gap={4} wrap="wrap">
            {orig.logo_url && (
              <Image
                src={orig.logo_url}
                alt="Logo"
                maxH="48px"
                maxW="120px"
                borderWidth="1px"
                borderRadius="md"
                objectFit="contain"
              />
            )}
            <Box>
              <Text fontWeight="semibold">{orig.display_name || orig.slug}</Text>
              <Text fontSize="sm" color="gray.500">
                /{orig.slug}
              </Text>
            </Box>
            <Box flex="1" minW="200px">
              <Text fontSize="xs" color="gray.500" mb={1}>
                Default pricing
              </Text>
              <HStack spacing={4} fontSize="sm">
                <Text>
                  Creator:{" "}
                  {orig.default_pricing?.creator_verification == null
                    ? "—"
                    : `$${(orig.default_pricing.creator_verification / 100).toFixed(2)}`}
                </Text>
                <Text>
                  Work:{" "}
                  {orig.default_pricing?.work_certification == null
                    ? "—"
                    : `$${(orig.default_pricing.work_certification / 100).toFixed(2)}`}
                </Text>
                <Text>
                  Free works:{" "}
                  {orig.default_pricing?.work_certification === 0
                    ? "Unlimited"
                    : orig.default_pricing?.free_works ?? "—"}
                </Text>
              </HStack>
            </Box>
          </Flex>
          <Text fontSize="sm" color="gray.500">
            Origin ID: <Code fontSize="xs">{orig.id}</Code>
            {"  ·  "}Created: {fmtDate(orig.created_at)}
            {"  ·  "}Updated: {fmtDate(orig.updated_at)}
            <Tooltip label="Copy Origin ID">
              <IconButton
                aria-label="Copy Origin ID"
                icon={<CopyIcon />}
                size="xs"
                variant="ghost"
                ml={1}
                onClick={copyId}
              />
            </Tooltip>
          </Text>
        </Stack>
      )}

      <Box mt={6}>
        <Heading size="sm" mb={2}>
          Events
        </Heading>
        <OriginEventsList originId={orig.id} originSlug={orig.slug} />
      </Box>
    </Box>
  );
}
